import type { Ref } from 'vue';

import type { IContextQueryResult } from '@modules/core/services/query/types';
import type { Permission } from '@modules/permissions/utils';
import type { ITableDataResponse, ITableState } from '@modules/tables/types';
import { useRestClient } from '@modules/core/services/api';
import { REPORTS_TIMEOUT } from '@modules/core/services/api/constants';
import { useContextQuery } from '@modules/core/services/query';
import { keepPreviousData } from '@tanstack/vue-query';

import type { IReportPayload } from '../components/VReports/types';
import type { ReportType } from '../enums';
import { REPORT_GENERATE_API_URL, REPORT_GENERATE_QUERY } from '../constants';

export default function useReportGenerateQuery(
  tableState: Ref<ITableState>,
  reportType: Ref<ReportType>,
  permission: Ref<Permission>,
  payload: Ref<IReportPayload | undefined>,
): IContextQueryResult<
    ITableDataResponse<Record<string, string | number>>,
    Error
  > {
  const { fetchPost } = useRestClient();

  return useContextQuery<ITableDataResponse<Record<string, string | number>>>({
    queryKey: [REPORT_GENERATE_QUERY, reportType, tableState],
    queryFn: ({ signal }) => fetchPost<ITableDataResponse<Record<string, string | number>>>(
      REPORT_GENERATE_API_URL,
      { ...tableState.value, report: payload.value },
      {
        signal,
        permission: permission.value,
        timeout: REPORTS_TIMEOUT,
        urlParams: {
          reportType: reportType.value,
        },
      },
    ),
    placeholderData: keepPreviousData,
    retry: false,
    enabled: false,
  });
}
